// // src/api/company.ts
import axios from "axios";
import { CompanyApiResponse } from "../types/companyTypes";
import * as Sentry from "@sentry/react";

interface CreateCompanyRequest {
  kbo_number: string;
  company_legal_name: string;
  company_vat: string;
  company_founded: string;
  company_nace_code: string;
  company_legal_form: string;
  company_email: string;
  company_phone: string;
  company_website: string;
  company_address_street: string;
  company_address_number: string;
  company_address_zip: string;
  company_address_city: string;
  company_address_country: string;
  user_id: string;
  billing_email: string;
  billing_address_street: string;
  billing_address_number: string;
  billing_address_zip: string;
  billing_address_city: string;
  billing_address_country: string;
  invoicing_cycle: string;
  iban: string;
}

export async function searchCompany(cleanedTerm: string, token: string): Promise<CompanyApiResponse> {
  try {
    const res = await fetch(
      `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company/search/${encodeURIComponent(cleanedTerm)}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok) {
      const error = new Error("Company not found");
      Sentry.captureException(error);
      throw error;
    }
    const data: CompanyApiResponse = await res.json();
    if (!data?.companies || data.companies.length === 0) {
      const error = new Error("No companies found");
      Sentry.captureException(error);
      throw error;
    }
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}

export async function createCompany(payload: CreateCompanyRequest, token: string): Promise<CompanyApiResponse> {
  try {
    const response = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // Create a custom error object
      const error = new Error() as any;
      error.response = {
        status: response.status,
        data: await response.json().catch(() => ({})),
      };

      // Set specific error messages based on status code
      switch (response.status) {
        case 409:
          error.message = "Company already exists";
          break;
        case 401:
          error.message = "Unauthorized access";
          break;
        case 500:
          error.message = "Internal server error";
          break;
        default:
          error.message = "Failed to add the company";
      }

      Sentry.captureException(error);
      throw error;
    }

    return response.json();
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}

interface AccessRequestPayload {
  companyName: string;
  companyVat: string;
  message: string;
  kbo_number: string;
}

export async function sendAccessRequest(payload: AccessRequestPayload, token: string): Promise<void> {
  const response = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company/mail`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // Create an error and add a custom "response"
    const error = new Error("Failed to send access request") as any;
    // Attach the status and any parsed response data
    error.response = {
      status: response.status,
      data: await response.json().catch(() => ({})), // parse body or return an empty object
    };
    throw error;
  }
}

export const checkCompanyExists = async (kbo_number: string, token: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/company/check/${encodeURIComponent(kbo_number)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("checkCompanyExists response:", response.data);

    // Simply return the response data since the API already returns
    // the correct format: { exists: boolean, company: {...} }
    return response.data;
  } catch (error) {
    console.error("Error in checkCompanyExists:", error);
    throw error;
  }
};
